import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import './toast.css';

export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

const ToastComponent = ({ message, type, duration, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const onCloseRef = useRef();

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => onCloseRef.current(), 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  if (!isVisible) return null;

  const getToastClass = () => {
    switch(type) {
      case TOAST_TYPES.SUCCESS:
        return 'toast-success';
      case TOAST_TYPES.ERROR:
        return 'toast-error';
      case TOAST_TYPES.WARNING:
        return 'toast-warning';
      default:
        return 'toast-info';
    }
  };

  const getIcon = () => {
    switch(type) {
      case TOAST_TYPES.SUCCESS: return '✓';
      case TOAST_TYPES.ERROR: return '✕';
      case TOAST_TYPES.WARNING: return '⚠';
      default: return 'ℹ';
    }
  };

  return ReactDOM.createPortal(
    <div className="toast-container">
      <div className={`toast ${getToastClass()}`}>
        <span className="toast-icon">{getIcon()}</span>
        <span className="toast-message">{message}</span>
        <button 
          className="toast-close"
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
        >
          ✕
        </button>
      </div>
    </div>,
    document.body
  );
};

export const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = TOAST_TYPES.INFO, duration = 3000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);
    return id;
  }, []);

  const hideToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const ToastContainer = useCallback(() => (
    <>
      {toasts.map(toast => (
        <ToastComponent
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => hideToast(toast.id)}
        />
      ))}
    </>
  ), [toasts, hideToast]);

  return { showToast, ToastContainer };
};